import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, X, Send, Bot, Trash2 } from "lucide-react";
import ChatBubble from "./ChatBubble";
import { supabase } from "@/integrations/supabase/client";
import { blogPosts } from "@/data/blogPosts";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "react-router-dom";

interface Message {
  role: "user" | "assistant";
  content: string;
  articles?: any[];
  suggestedReplies?: string[];
}

interface FloatingChatWidgetProps {
  pageContext?: string;
}

const STORAGE_KEY = "globaltis_chat_data";

const getSessionId = (): string => {
  const key = "globaltis_chat_session_id";
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

const saveChatMessage = async (sessionId: string, role: string, content: string, pageContext: string) => {
  try {
    await supabase.from("chat_messages" as any).insert({
      session_id: sessionId,
      role,
      content,
      page_context: pageContext,
    });
  } catch (err) {
    console.error("Error saving chat message:", err);
  }
};

const initialMessage: Message = {
  role: "assistant",
  content:
    "¡Hola! 👋 Soy el asistente de Globaltis. Puedo ayudarte con:\n\n• Explicarte qué servicios ofrecemos\n• Recomendarte soluciones para tu negocio\n• Mostrarte casos de éxito similares\n\n¿En qué puedo ayudarte hoy?",
};

const FloatingChatWidget = ({ pageContext }: FloatingChatWidgetProps) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [currentInput, setCurrentInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Detectar contexto de página automáticamente
  const currentPageContext = pageContext || (() => {
    if (location.pathname === "/") return "index";
    if (location.pathname.startsWith("/blog")) return "blog";
    return "index";
  })();

  // Cargar datos del localStorage al montar
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.messages && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
          setMessages(parsed.messages);
        }
        if (typeof parsed.isOpen === "boolean") {
          setIsOpen(parsed.isOpen);
        }
      }
    } catch (error) {
      console.error("Error loading chat data from localStorage:", error);
    }
  }, []);

  // Guardar datos en localStorage cuando cambien
  useEffect(() => {
    try {
      const dataToSave = {
        messages,
        isOpen,
        lastUpdate: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error("Error saving chat data to localStorage:", error);
    }
  }, [messages, isOpen]);

  const quickReplies = [
    "🦷 Trabajo en una clínica dental",
    "🍕 Tengo un restaurante",
    "💬 Necesito un bot de WhatsApp",
    "🤖 ¿Qué es un agente de voz?",
  ];

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const getPageContent = () => {
    const pageContents: Record<string, string> = {
      index: `
**Globaltis - Servicios principales:**
- Asistentes de voz personalizados: Agentes que atienden llamadas, explican productos, toman pedidos. Ejemplos: Asistente Técnico, Agente de Pedidos, Secretaria Virtual, Agente Comercial.
- Automatizaciones inteligentes: Presupuestos, correos, facturas que se generan automáticamente.
- Cápsulas IA: Soluciones probadas semanalmente que se convierten en herramientas listas para usar.

**CEO:** Juan Carlos, CEO de Globaltis desde 2003.
**Propuesta de valor:** "Cada día pruebo herramientas de IA para que tú no tengas que hacerlo. Solo IA que funciona (probada por nosotros primero)."
**Clientes:** 50+ pequeñas empresas, 100+ autónomos.
**Filosofía:** Sin tecnicismos, sin complicaciones, sin compromiso.
      `,
      blog: "Página de blog con artículos sobre IA para pequeñas empresas y autónomos."
    };

    return pageContents[currentPageContext] || "";
  };

  const clearConversation = () => {
    setMessages([initialMessage]);
    toast({
      title: "Conversación reiniciada",
      description: "Has comenzado una nueva conversación.",
    });
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const sessionId = getSessionId();
    const trimmed = content.trim();
    const userMessage: Message = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setCurrentInput("");
    setIsLoading(true);

    // Fire-and-forget save
    saveChatMessage(sessionId, "user", trimmed, currentPageContext);

    try {
      const articlesData = blogPosts.map((post) => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        description: post.description,
        category: post.category,
        departments: post.departments,
        image: post.image,
        content: post.content ? post.content.substring(0, 2000) + "..." : "",
        insight: post.insight || ""
      }));

      const { data, error } = await supabase.functions.invoke("chat-assistant", {
        body: {
          messages: [...messages, userMessage].map((m) => ({
            role: m.role,
            content: m.content,
          })),
          pageContext: currentPageContext,
          pageContent: getPageContent(),
          articles: articlesData,
          currentUrl: window.location.href,
          pageTitle: document.title,
        },
      });

      if (error) throw error;

      // If contact was captured, save to contact_submissions
      if (data.contactCaptured && data.capturedContact) {
        try {
          await supabase.from("contact_submissions" as any).insert({
            nombre: data.capturedContact.nombre,
            email: data.capturedContact.telefono, // storing phone in email field temporarily
            empresa: null,
            mensaje: `[Chat Bot] Tel: ${data.capturedContact.telefono} - ${data.capturedContact.contexto || "Contacto desde chat"}`,
            source: "chat_bot",
          });
          console.log("Contact captured from chat:", data.capturedContact.nombre);
        } catch (saveErr) {
          console.error("Error saving captured contact:", saveErr);
        }
      }

      // Extract quick replies from message if present
      let cleanMessage = data.message;
      let suggestedReplies: string[] = [];
      const qrMatch = cleanMessage.match(/\[QR:\s*"([^"]+)"(?:\s*\|\s*"([^"]+)")?(?:\s*\|\s*"([^"]+)")?\]/);
      if (qrMatch) {
        cleanMessage = cleanMessage.replace(qrMatch[0], "").trim();
        suggestedReplies = [qrMatch[1], qrMatch[2], qrMatch[3]].filter(Boolean);
      }

      const assistantMessage: Message = {
        role: "assistant",
        content: cleanMessage,
        articles: data.recommendedArticles,
        suggestedReplies,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Fire-and-forget save assistant response
      saveChatMessage(sessionId, "assistant", data.message, currentPageContext);
    } catch (error) {
      console.error("Error calling chat assistant:", error);
      toast({
        title: "Error",
        description: "No pude procesar tu mensaje. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (reply: string) => {
    sendMessage(reply);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(currentInput);
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <Button
          data-chat-widget
          onClick={() => setIsOpen(true)}
          className="h-14 px-4 sm:h-16 sm:px-6 rounded-full shadow-2xl bg-gradient-to-r from-primary to-accent hover:scale-105 transition-all duration-300 animate-bounce-subtle"
          size="lg"
        >
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="font-semibold text-sm sm:text-base hidden sm:inline">¿Dudas?</span>
          </div>
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-[380px] sm:max-w-[400px] h-[500px] max-h-[80vh] sm:h-[600px] sm:max-h-[70vh] flex flex-col bg-background rounded-2xl shadow-2xl border border-border overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-primary to-accent text-primary-foreground">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 sm:w-6 sm:h-6" />
          <div>
            <h3 className="text-sm sm:text-base font-semibold">Asistente Globaltis</h3>
            <p className="text-[10px] sm:text-xs opacity-90">Siempre disponible</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={clearConversation}
            className="text-primary-foreground hover:bg-primary-foreground/20"
            title="Limpiar conversación"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="text-primary-foreground hover:bg-primary-foreground/20"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 p-3 sm:p-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <ChatBubble
              key={index}
              role={message.role}
              content={message.content}
              articles={message.articles}
            />
          ))}

          {/* Quick Replies - Show initial or dynamic */}
          {messages.length === 1 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {quickReplies.map((reply) => (
                <Button
                  key={reply}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickReply(reply)}
                  className="text-[10px] sm:text-xs"
                  disabled={isLoading}
                >
                  {reply}
                </Button>
              ))}
            </div>
          )}

          {/* Dynamic Quick Replies from bot */}
          {messages.length > 1 && !isLoading && (() => {
            const lastAssistant = [...messages].reverse().find(m => m.role === "assistant");
            if (lastAssistant?.suggestedReplies && lastAssistant.suggestedReplies.length > 0) {
              return (
                <div className="flex flex-wrap gap-2 mt-4">
                  {lastAssistant.suggestedReplies.map((reply) => (
                    <Button
                      key={reply}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickReply(reply)}
                      className="text-[10px] sm:text-xs"
                    >
                      {reply}
                    </Button>
                  ))}
                </div>
              );
            }
            return null;
          })()}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex items-start gap-2">
              <div className="bg-muted text-foreground rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-3 sm:p-4 border-t border-border bg-muted/30">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            placeholder="Escribe tu pregunta..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button type="submit" size="icon" disabled={isLoading || !currentInput.trim()}>
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default FloatingChatWidget;
